<script setup lang="ts">
import { defineProps, ref, computed, nextTick } from "vue";
import { ElementBalance } from "@/types/calculators";
import { elementColors, elementLabels } from "@/constants/bazi";
import { useI18n } from "vue-i18n";

const props = defineProps<{ data: ElementBalance[] }>();
const { t } = useI18n();

const tooltip = ref<{
  visible: boolean;
  x: number;
  y: number;
  selectedIndex: number;
}>({
  visible: false,
  x: 0,
  y: 0,
  selectedIndex: 0,
});
const containerRef = ref<HTMLDivElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const extraShift = ref(0);

const labels = computed(() => props.data.map((e) => e.element));

const maxValue = 5;
const radius = 180;
const center = 225;

// 5 углов — равномерно по окружности
function getPoint(idx: number, value: number, scale = 1) {
  const angle = ((Math.PI * 2) / 5) * idx - Math.PI / 2;
  const r = (value / maxValue) * radius * scale;
  return {
    x: center + Math.cos(angle) * r,
    y: center + Math.sin(angle) * r,
  };
}

// Контур "заполненного" многоугольника
const polygonPoints = computed(() =>
  props.data
    .map((e, i) => {
      const { x, y } = getPoint(i, Math.max(0, Math.min(maxValue, e.value)));
      return `${x},${y}`;
    })
    .join(" ")
);

// Координаты для подписей (немного дальше)
const labelPoints = computed(() =>
  props.data.map((_, i) => getPoint(i, 1.17 * maxValue))
);

function showTip(i: number, evt: MouseEvent) {
  const target = evt.target as SVGGraphicsElement;
  const rect = target.getBoundingClientRect();
  const containerRect = containerRef.value?.getBoundingClientRect();

  if (!containerRect) return;

  tooltip.value = {
    visible: true,
    x: rect.left - containerRect.left + rect.width / 2,
    y: rect.bottom - containerRect.top + 8,
    selectedIndex: i,
  };
  nextTick(adjustPosition);
}

function hideTip() {
  tooltip.value.visible = false;
  extraShift.value = 0;
}

function adjustPosition() {
  const rect = tooltipRef.value?.getBoundingClientRect();
  if (!rect) return;

  extraShift.value = 0;
  if (rect.right > window.innerWidth) {
    extraShift.value = window.innerWidth - rect.right - 8;
  }
  if (rect.left < 0) {
    extraShift.value = -rect.left + 8;
  }
}
</script>

<template>
  <div class="flex justify-center">
    <div class="w-full sm:w-[480px] relative" ref="containerRef">
      <svg
        class="bg-transparent w-full h-auto"
        :viewBox="`0 0 ${center * 2} ${center * 2}`"
        preserveAspectRatio="xMidYMid meet"
      >
        <!-- Сетка -->
        <g v-for="step in 5" :key="step">
          <polygon
            :points="
              props.data
                .map((_, i) => {
                  const p = getPoint(i, step);
                  return `${p.x},${p.y}`;
                })
                .join(' ')
            "
            fill="none"
            stroke="#000"
            :stroke-width="step === 5 ? 2 : 1"
            opacity="0.5"
          />
        </g>

        <!-- Линии от центра к вершинам -->
        <g>
          <line
            v-for="(_, i) in labels"
            :key="i"
            :x1="center"
            :y1="center"
            :x2="getPoint(i, maxValue).x"
            :y2="getPoint(i, maxValue).y"
            stroke="#000"
            stroke-dasharray="3,2"
            stroke-width="1"
          />
        </g>

        <!-- Заполненный полигон -->
        <polygon
          :points="polygonPoints"
          fill="#0dcaf0"
          fill-opacity="0.55"
          stroke="#0dcaf0"
          stroke-width="2"
        />

        <!-- Подписи -->
        <g>
          <text
            v-for="(pt, i) in labelPoints"
            :key="i"
            :x="pt.x"
            :y="pt.y"
            text-anchor="middle"
            alignment-baseline="middle"
            class="cursor-pointer pointer-events-auto text-[24px] font-bold"
            :fill="elementColors[props.data[i].element]"
            @mouseenter="showTip(i, $event)"
            @mouseleave="hideTip"
          >
            {{ labels[i] }}
          </text>
        </g>
      </svg>

      <!-- Tooltip -->
      <div
        v-if="tooltip.visible"
        ref="tooltipRef"
        class="absolute z-50 rounded-[10px] bg-brown text-white px-3 py-2 text-left"
        :style="{
          left: tooltip.x + 'px',
          top: tooltip.y + 'px',
          transform: `translateX(-50%) translateX(${extraShift}px)`,
        }"
      >
        <p class="text-center">
          {{
            elementLabels[props.data[tooltip.selectedIndex].element] ||
            props.data[tooltip.selectedIndex].element
          }}
        </p>
        <p>
          {{
            t("calculators.bazi.results.element-balance.hexagram.meaning", {
              meaning: props.data[tooltip.selectedIndex].value,
            })
          }}
        </p>
        <p v-if="props.data[tooltip.selectedIndex].role == 'unfavorable'">
          {{
            t(
              "calculators.bazi.results.element-balance.hexagram.unfavourable-role"
            )
          }}
        </p>
        <p v-else>
          {{
            t(
              "calculators.bazi.results.element-balance.hexagram.favourable-role"
            )
          }}
        </p>
      </div>
    </div>
  </div>
</template>
